/**
 * ColumnRuler + RowRuler — Iter 198g / 198h.
 *
 * Figma-style dual rulers that appear during drag:
 *   • Top ruler: 24 major columns, active column highlighted, playhead
 *     drops down through the section.
 *   • Left ruler: rows in 20px units (matches GRID_ROW_PX from
 *     useFluidTextDrag), active row highlighted, playhead extends right
 *     through the section.
 *
 * Both are portal-rendered to document.body and share the same
 * `wb:drag-start / wb:drag-move / wb:drag-end` event stream from
 * `useFluidTextDrag`, so they light up in perfect sync.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const COLS = 24;
const ROW_PX = 20;                    // must match GRID_ROW_PX
const MAJOR_ROW_EVERY = 4;            // heavier tick every 4 rows (80px)

export default function ColumnRuler() {
  const [state, setState] = useState({
    visible: false,
    sectionRect: null,
    activeCol: -1,
    activeRow: -1,
    offsetPx: 0,
    offsetPy: 0,
  });

  useEffect(() => {
    const onStart = (e) => {
      const sr = e.detail?.sectionRect;
      if (!sr) return;
      setState({ visible: true, sectionRect: sr, activeCol: -1, activeRow: -1, offsetPx: 0, offsetPy: 0 });
    };
    const onMove = (e) => {
      const sr = e.detail?.sectionRect;
      const cx = e.detail?.elCenterX;
      const cy = e.detail?.elCenterY;
      if (!sr) return;
      const nextState = { visible: true, sectionRect: sr };
      if (typeof cx === 'number') {
        const relX = Math.max(0, Math.min(sr.width, cx - sr.left));
        nextState.activeCol = Math.max(0, Math.min(COLS, Math.round((relX / sr.width) * COLS)));
        nextState.offsetPx = Math.round(relX);
      }
      if (typeof cy === 'number') {
        const relY = Math.max(0, Math.min(sr.height, cy - sr.top));
        nextState.activeRow = Math.max(0, Math.round(relY / ROW_PX));
        nextState.offsetPy = Math.round(relY);
      }
      setState(s => ({ ...s, ...nextState }));
    };
    const onEnd = () =>
      setState({ visible: false, sectionRect: null, activeCol: -1, activeRow: -1, offsetPx: 0, offsetPy: 0 });

    window.addEventListener('wb:drag-start', onStart);
    window.addEventListener('wb:drag-move', onMove);
    window.addEventListener('wb:drag-end', onEnd);
    return () => {
      window.removeEventListener('wb:drag-start', onStart);
      window.removeEventListener('wb:drag-move', onMove);
      window.removeEventListener('wb:drag-end', onEnd);
    };
  }, []);

  if (!state.visible || !state.sectionRect) return null;

  const { top, left, width, height } = state.sectionRect;
  const rulerTop = Math.max(6, top - 28);
  const rulerLeft = Math.max(6, left - 28);
  const totalRows = Math.max(1, Math.round(height / ROW_PX));

  return createPortal(
    <>
      {/* ── TOP COLUMN RULER ──────────────────────────────────────── */}
      <div
        className="fixed z-[9998] pointer-events-none select-none"
        style={{ top: rulerTop, left, width, height: 22 }}
        data-testid="column-ruler"
      >
        <div
          className="absolute inset-0 rounded-md border border-white/[0.08]"
          style={{
            background: 'rgba(20,16,25,0.92)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 24px -4px rgba(0,0,0,0.5)',
          }}
        />
        <div className="absolute inset-0 flex" data-testid="column-ruler-cells">
          {Array.from({ length: COLS }).map((_, i) => {
            const isActive = i === state.activeCol - 1 || i === state.activeCol;
            const isMajor = i % 4 === 0;
            return (
              <div
                key={i}
                className={`relative flex-1 h-full border-r last:border-r-0 transition-colors duration-75 ${
                  isActive
                    ? 'bg-[#22D3EE]/25 border-r-[#22D3EE]/70'
                    : isMajor
                    ? 'border-r-white/25'
                    : 'border-r-white/10'
                }`}
              />
            );
          })}
        </div>
        <div className="absolute inset-0 pointer-events-none">
          {[0, 4, 8, 12, 16, 20, 24].map(n => (
            <span
              key={n}
              className={`absolute -top-[10px] text-[8.5px] font-bold tabular-nums tracking-wider ${
                state.activeCol === n ? 'text-[#22D3EE]' : 'text-white/45'
              }`}
              style={{ left: `${(n / COLS) * 100}%`, transform: 'translateX(-50%)' }}
            >
              {n}
            </span>
          ))}
        </div>
        {state.activeCol >= 0 && (
          <div
            className="absolute -top-[26px] px-2 py-0.5 rounded-full border border-[#22D3EE]/50 text-[9px] font-bold tracking-[0.14em] uppercase whitespace-nowrap"
            style={{
              left: `${(state.activeCol / COLS) * 100}%`,
              transform: 'translateX(-50%)',
              background: 'rgba(20,16,25,0.96)',
              color: '#22D3EE',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4), 0 0 8px rgba(34,211,238,0.4)',
            }}
            data-testid="column-ruler-readout"
          >
            Col {state.activeCol}/{COLS} · {state.offsetPx}px
          </div>
        )}
        {state.activeCol >= 0 && (
          <div
            className="absolute top-full w-px"
            style={{
              left: `${(state.activeCol / COLS) * 100}%`,
              height,
              background: 'linear-gradient(to bottom, #22D3EE, rgba(34,211,238,0))',
              boxShadow: '0 0 8px rgba(34,211,238,0.5)',
              transform: 'translateX(-0.5px)',
            }}
            aria-hidden="true"
          />
        )}
      </div>

      {/* ── LEFT ROW RULER ────────────────────────────────────────── */}
      <div
        className="fixed z-[9998] pointer-events-none select-none"
        style={{ top, left: rulerLeft, width: 22, height }}
        data-testid="row-ruler"
      >
        <div
          className="absolute inset-0 rounded-md border border-white/[0.08]"
          style={{
            background: 'rgba(20,16,25,0.92)',
            backdropFilter: 'blur(10px)',
            boxShadow: '8px 0 24px -4px rgba(0,0,0,0.5)',
          }}
        />
        {/* Row ticks — every 20px, brighter every 4th (80px) */}
        <div className="absolute inset-0" data-testid="row-ruler-cells">
          {Array.from({ length: totalRows }).map((_, i) => {
            const isActive = i === state.activeRow - 1 || i === state.activeRow;
            const isMajor = i % MAJOR_ROW_EVERY === 0;
            return (
              <div
                key={i}
                className={`absolute left-0 right-0 border-b transition-colors duration-75 ${
                  isActive
                    ? 'bg-[#22D3EE]/25 border-b-[#22D3EE]/70'
                    : isMajor
                    ? 'border-b-white/25'
                    : 'border-b-white/10'
                }`}
                style={{ top: i * ROW_PX, height: ROW_PX }}
              />
            );
          })}
        </div>
        {/* Major row labels every 4 rows (80px). Cap to reasonable number
            so we don't spam pixels on a very tall section. */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: Math.min(totalRows, 40) }).map((_, i) => {
            if (i % MAJOR_ROW_EVERY !== 0) return null;
            return (
              <span
                key={i}
                className={`absolute -left-[10px] text-[8.5px] font-bold tabular-nums tracking-wider ${
                  state.activeRow === i ? 'text-[#22D3EE]' : 'text-white/45'
                }`}
                style={{ top: i * ROW_PX, transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'center' }}
              >
                {i}
              </span>
            );
          })}
        </div>
        {state.activeRow >= 0 && (
          <div
            className="absolute px-2 py-0.5 rounded-full border border-[#22D3EE]/50 text-[9px] font-bold tracking-[0.14em] uppercase whitespace-nowrap"
            style={{
              top: `${(state.activeRow * ROW_PX)}px`,
              left: '-90px',
              transform: 'translateY(-50%)',
              background: 'rgba(20,16,25,0.96)',
              color: '#22D3EE',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4), 0 0 8px rgba(34,211,238,0.4)',
            }}
            data-testid="row-ruler-readout"
          >
            Row {state.activeRow} · {state.offsetPy}px
          </div>
        )}
        {/* Row playhead — thin bright line extending RIGHT through the section */}
        {state.activeRow >= 0 && (
          <div
            className="absolute left-full h-px"
            style={{
              top: `${state.activeRow * ROW_PX}px`,
              width,
              background: 'linear-gradient(to right, #22D3EE, rgba(34,211,238,0))',
              boxShadow: '0 0 8px rgba(34,211,238,0.5)',
              transform: 'translateY(-0.5px)',
            }}
            aria-hidden="true"
          />
        )}
      </div>

      {/* Corner accent — small cyan square where the two rulers meet.
          Purely decorative; makes the crosshair feel intentional. */}
      <div
        className="fixed z-[9998] pointer-events-none"
        style={{
          top: rulerTop,
          left: rulerLeft,
          width: 22,
          height: 22,
          background: 'rgba(34,211,238,0.15)',
          border: '1px solid rgba(34,211,238,0.35)',
          borderRadius: 4,
          boxShadow: '0 0 8px rgba(34,211,238,0.3)',
        }}
        aria-hidden="true"
      />
    </>,
    document.body
  );
}
